import upperFirst from "lodash/upperFirst";
import React, { type FC } from "react";

import { Truncated } from "~/components/truncated";
import { HealthStateValue } from "~/models/dto";

import type { FullComponent } from "../../../common/openapi";
import type { IconSize } from "../icon/IconSizes";

import { StateIcon } from "./StateIcon";
import { type SimpleStatefulComponent, fullToSimpleStatefulComponent } from "./state-component-utils";

/**
 * Component for which the component type, icon and state are resolved.
 */

const toSimpleStatefulComponent = (
	component: undefined | FullComponent | SimpleStatefulComponent,
): undefined | SimpleStatefulComponent => {
	if (component && "data" in component) {
		return fullToSimpleStatefulComponent(component);
	}
	return component;
};

export interface StateComponentProps {
	component?: SimpleStatefulComponent | FullComponent;
	displayName?: boolean;
	displayTypeName?: boolean;
	typeOfData?: string;
	iconSize?: IconSize;
}

export const StateComponent: FC<StateComponentProps> = ({
	component,
	displayName = false,
	displayTypeName = false,
	iconSize,
	typeOfData,
	...props
}) => {
	const simplifiedComponent = toSimpleStatefulComponent(component);
	const displayNameText = simplifiedComponent
		? displayTypeName && simplifiedComponent.typeName != null
			? `${simplifiedComponent.name} (${simplifiedComponent.typeName})`
			: simplifiedComponent.name
		: null;

	return (
		<div
			data-component-name={simplifiedComponent?.name}
			data-testid="state-component"
			{...props}
			className="flex max-w-full items-center gap-4"
		>
			{simplifiedComponent ? (
				<StateIcon
					healthStateValue={simplifiedComponent.healthState}
					icon={simplifiedComponent.icon}
					iconSize={iconSize}
				/>
			) : (
				<StateIcon healthStateValue={HealthStateValue.Unknown} iconSize={iconSize} />
			)}

			{/* min-w-0 fixes flexbug with content overflow */}
			{/* empty:hidden is necessary for preventing gap-* from the parent node */}
			<div className="flex min-w-0 gap-2 font-medium text-base empty:hidden">
				{displayName && <Truncated truncate="middle">{displayNameText ?? "Component is not defined"}</Truncated>}

				{displayName && simplifiedComponent?.name && typeOfData && <span>-</span>}

				{typeOfData && <Truncated>{upperFirst(typeOfData)}</Truncated>}
			</div>
		</div>
	);
};
